import { useMemo } from 'react';
import {
  FOG_800M_COLOR,
  FOG_1600M_COLOR,
  FOG_3000M_COLOR,
  NO_FOG_COLOR,
  NO_DATA_COLOR
} from '../../theme/colors';
import { ALL_AIRPORTS_DATA } from '../../data/locations';

export interface FogStatus {
  severity: 'severe' | 'moderate' | 'light' | 'none' | 'nodata';
  color: string;
  title: string;
  message: string;
  airports: string[];
}

/**
 * Custom hook to calculate overall fog status based on airport colors
 * Extracts the fog status calculation logic from MapPage
 */
export function useFogStatus(
  airportColors: Record<number, string>,
  dataAirports: number[]
): FogStatus {
  return useMemo(() => {
    const foggedAirports: { [key: string]: string[] } = {
      severe: [], // red - ≤800m
      moderate: [], // orange - ≤1600m
      light: [], // yellow - ≤3000m
      clear: [], // green - no fog
      noData: [] // gray - no data
    };

    // Check data airports only (the ones we fetch forecasts for)
    dataAirports.forEach((airportId) => {
      const airport = ALL_AIRPORTS_DATA.find(a => a.id === airportId);
      if (airport) {
        const color = airportColors[airportId];
        const displayName = `${airport.name} (${airport.code})`;
        if (color === FOG_800M_COLOR) {
          foggedAirports.severe.push(displayName);
        } else if (color === FOG_1600M_COLOR) {
          foggedAirports.moderate.push(displayName);
        } else if (color === FOG_3000M_COLOR) {
          foggedAirports.light.push(displayName);
        } else if (color === NO_FOG_COLOR) {
          foggedAirports.clear.push(displayName);
        } else if (color === NO_DATA_COLOR || !color) {
          foggedAirports.noData.push(displayName);
        }
      }
    });

    // Debug: log categorized airports
    console.log('[useFogStatus] Categorized airports:', {
      severe: foggedAirports.severe,
      moderate: foggedAirports.moderate,
      light: foggedAirports.light,
      clear: foggedAirports.clear,
      noData: foggedAirports.noData
    });

    // Check if all airports have no data
    if (foggedAirports.noData.length === dataAirports.length) {
      return {
        severity: 'nodata',
        color: NO_DATA_COLOR,
        title: 'No Data Available',
        message: 'Unable to retrieve forecast data from monitored airports',
        airports: foggedAirports.noData
      };
    }

    // Determine worst case
    if (foggedAirports.severe.length > 0) {
      return {
        severity: 'severe',
        color: FOG_800M_COLOR,
        title: 'Low Visibility Forecast (≤800m)',
        message: `Visibility ≤ 800m at: ${foggedAirports.severe.join(', ')}`,
        airports: foggedAirports.severe
      };
    } else if (foggedAirports.moderate.length > 0) {
      return {
        severity: 'moderate',
        color: FOG_1600M_COLOR,
        title: 'Low Visibility Forecast (≤1600m)',
        message: `Visibility ≤ 1600m at: ${foggedAirports.moderate.join(', ')}`,
        airports: foggedAirports.moderate
      };
    } else if (foggedAirports.light.length > 0) {
      return {
        severity: 'light',
        color: FOG_3000M_COLOR,
        title: 'Low Visibility Forecast (≤3000m)',
        message: `Visibility ≤ 3000m at: ${foggedAirports.light.join(', ')}`,
        airports: foggedAirports.light
      };
    } else {
      return {
        severity: 'none',
        color: NO_FOG_COLOR,
        title: 'Clear Visibility Conditions',
        message: 'Clear visibility forecast at all airports',
        airports: []
      };
    }
  }, [airportColors, dataAirports]);
}